import { motion } from "framer-motion";
import { Check, X, Minus } from "lucide-react";

type CellValue = "yes" | "no" | "partial" | string;

interface ComparisonRow {
    feature: string;
    manual: CellValue;
    genericATS: CellValue;
    intellr: CellValue;
}

const rows: ComparisonRow[] = [
    { feature: "AI-Powered Sourcing", manual: "no", genericATS: "partial", intellr: "yes" },
    { feature: "Voice Interview Screening", manual: "no", genericATS: "no", intellr: "yes" },
    { feature: "Automated Candidate Scoring", manual: "no", genericATS: "partial", intellr: "yes" },
    { feature: "Bias-Free Evaluation", manual: "no", genericATS: "no", intellr: "yes" },
    { feature: "Smart Job Matching", manual: "no", genericATS: "partial", intellr: "yes" },
    { feature: "Pipeline Automation", manual: "no", genericATS: "partial", intellr: "yes" },
    { feature: "Avg. Time-to-Hire", manual: "45 days", genericATS: "28 days", intellr: "12 days" },
    { feature: "Cost per Hire", manual: "₹85,000", genericATS: "₹55,000", intellr: "₹22,000" },
];

const renderCell = (value: CellValue, isIntellr = false) => {
    if (value === "yes") {
        return (
            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isIntellr ? "bg-accent/10" : "bg-emerald-50"}`}>
                <Check className={`w-4 h-4 ${isIntellr ? "text-accent" : "text-emerald-500"}`} />
            </div>
        );
    }
    if (value === "no") {
        return (
            <div className="w-7 h-7 rounded-full bg-rose-50 flex items-center justify-center">
                <X className="w-4 h-4 text-rose-400" />
            </div>
        );
    }
    if (value === "partial") {
        return (
            <div className="w-7 h-7 rounded-full bg-amber-50 flex items-center justify-center">
                <Minus className="w-4 h-4 text-amber-500" />
            </div>
        );
    }
    return (
        <span className={`text-sm font-bold ${isIntellr ? "text-accent" : "text-slate-600"}`}>{value}</span>
    );
};

const ComparisonSection = () => {
    return (
        <section className="py-20 sm:py-32 bg-white relative overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent/3 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center mb-12 sm:mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-50 text-slate-500 text-xs font-bold uppercase tracking-wider mb-5 border border-slate-200"
                    >
                        Why Intellr.
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="font-display text-3xl sm:text-4xl md:text-6xl font-bold text-slate-900 mb-6"
                    >
                        Not Another Tool. <br />
                        <span className="text-accent italic">An AI Hiring Partner.</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto font-medium"
                    >
                        See how Intellr. stacks up against manual hiring and traditional applicant tracking systems.
                    </motion.p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-4xl mx-auto"
                >
                    {/* Mobile layout: 4-column compact cards */}
                    <div className="sm:hidden">
                        <div className="grid grid-cols-4 gap-1 px-1 pb-3">
                            <div />
                            <div className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-wide">Manual</div>
                            <div className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-wide">ATS</div>
                            <div className="text-center text-[10px] font-bold text-accent uppercase tracking-wide">Intellr.</div>
                        </div>
                        <div className="space-y-2">
                            {rows.map((row) => (
                                <div key={row.feature} className="grid grid-cols-4 items-center gap-2 bg-white rounded-2xl border border-slate-100 shadow-sm px-3 py-3">
                                    <div>
                                        <span className="text-xs font-semibold text-slate-700 leading-snug block">{row.feature}</span>
                                    </div>
                                    <div className="flex justify-center">{renderCell(row.manual)}</div>
                                    <div className="flex justify-center">{renderCell(row.genericATS)}</div>
                                    <div className="flex justify-center relative">
                                        <div className="absolute inset-0 bg-accent/5 rounded-xl" />
                                        <div className="relative">{renderCell(row.intellr, true)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Desktop layout: full table */}
                    <div className="hidden sm:block rounded-3xl border border-slate-100 overflow-hidden bg-white shadow-lg shadow-slate-100/50">
                        {/* Header */}
                        <div className="grid grid-cols-4 gap-0 bg-slate-50/80">
                            <div className="p-5 border-b border-r border-slate-100">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Feature</span>
                            </div>
                            <div className="p-5 border-b border-r border-slate-100 text-center">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Manual Hiring</span>
                            </div>
                            <div className="p-5 border-b border-r border-slate-100 text-center">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Generic ATS</span>
                            </div>
                            <div className="p-5 border-b border-slate-100 text-center relative">
                                <div className="absolute inset-0 bg-accent/5" />
                                <span className="relative text-xs font-bold uppercase tracking-wider text-accent">Intellr.</span>
                            </div>
                        </div>

                        {/* Rows */}
                        {rows.map((row, i) => (
                            <div
                                key={row.feature}
                                className={`grid grid-cols-4 gap-0 ${i < rows.length - 1 ? "border-b border-slate-50" : ""} hover:bg-slate-50/50 transition-colors`}
                            >
                                <div className="p-5 border-r border-slate-100 flex items-center">
                                    <span className="text-sm font-semibold text-slate-700">{row.feature}</span>
                                </div>
                                <div className="p-5 border-r border-slate-100 flex items-center justify-center">
                                    {renderCell(row.manual)}
                                </div>
                                <div className="p-5 border-r border-slate-100 flex items-center justify-center">
                                    {renderCell(row.genericATS)}
                                </div>
                                <div className="p-5 flex items-center justify-center relative">
                                    <div className="absolute inset-0 bg-accent/[0.02]" />
                                    <div className="relative">{renderCell(row.intellr, true)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ComparisonSection;
